import { useEffect, useState } from 'react';
import { Loader2, Plus, Save, ShieldAlert, Trash2, X } from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import CropTypeCombobox from '../shared/CropTypeCombobox';
import cultivarService from '../../services/cultivar';
import pestModelService from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import { magareyWetnessRequired } from '../../utils/pestRisk';
import { defaultParams, TYPE_ICON, type PestDraft } from './pestModelDefaults';
import type {
  PestModel, PestModelInput, PestModelType, PestKind, DegreeDayParams, InfectionParams,
  GenericInfectionParams, RiskIndexParams, DegreeDayEvent, RiskLevel, PestProfile,
} from '../../types/pest';
import { RISK_LEVELS } from '../../types/pest';

export default function PestModelForm({ model, draft, canManage, onSaved, onCancel }: {
  /** The saved record being edited, or null when creating. */
  model: PestModel | null;
  draft: PestDraft;
  canManage: boolean;
  onSaved: (m: PestModel) => void;
  onCancel?: () => void;
}) {
  const readOnly = !canManage;

  const [name, setName] = useState(draft.name);
  const [commonName, setCommonName] = useState(draft.common_name ?? '');
  const [scientificName, setScientificName] = useState(draft.scientific_name ?? '');
  const [kind, setKind] = useState<PestKind>(draft.kind);
  const [description, setDescription] = useState(draft.description ?? '');
  const [modelType, setModelType] = useState<PestModelType>(draft.model_type);
  const [params, setParams] = useState<Record<string, unknown>>(
    (draft.model_params as Record<string, unknown>) ?? defaultParams(draft.model_type)
  );
  const [hostIds, setHostIds] = useState<number[]>(draft.host_crop_type_ids ?? []);
  const [recommended, setRecommended] = useState(draft.recommended_actions ?? '');
  const [active, setActive] = useState(draft.is_active ?? true);
  const profile: PestProfile | null = draft.profile ?? null;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Crop-type name resolution for host chips.
  const [cropTypeNames, setCropTypeNames] = useState<Record<number, string>>({});
  useEffect(() => {
    cultivarService.getCropTypes().then((r) => {
      if (r.success && r.data) {
        const map: Record<number, string> = {};
        for (const ct of r.data) map[ct.id] = ct.parent_id ? `${r.data.find((p) => p.id === ct.parent_id)?.name ?? ''} › ${ct.name}` : ct.name;
        setCropTypeNames(map);
      }
    }).catch(() => {});
  }, []);

  const onTypeChange = (t: PestModelType) => {
    setModelType(t);
    // Each archetype has its own parameter shape, so switching starts from its defaults.
    setParams(defaultParams(t));
  };

  const addHost = (id: number) => setHostIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const removeHost = (id: number) => setHostIds((prev) => prev.filter((h) => h !== id));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: PestModelInput = {
        name, common_name: commonName || null, scientific_name: scientificName || null, kind,
        description: description || null, model_type: modelType, model_params: params,
        recommended_actions: recommended || null, host_crop_type_ids: hostIds, is_active: active,
        profile,
      };
      const saved = model ? await pestModelService.updateModel(model.id, payload) : await pestModelService.createModel(payload);
      onSaved(saved);
    } catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <CardContent className="pt-5 space-y-4">
        {readOnly && (
          <div className="rounded-lg bg-violet-50 text-violet-800 px-3 py-2 text-sm">You need a spray admin licence to edit.</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name"><input disabled={readOnly} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Apple Black Spot" /></Field>
          <Field label="Common name"><input disabled={readOnly} value={commonName} onChange={(e) => setCommonName(e.target.value)} className={inputCls} /></Field>
          <Field label="Scientific name"><input disabled={readOnly} value={scientificName} onChange={(e) => setScientificName(e.target.value)} className={`${inputCls} italic`} placeholder="Venturia inaequalis" /></Field>
          <Field label="Type"><select disabled={readOnly} value={kind} onChange={(e) => setKind(e.target.value as PestKind)} className={inputCls}><option value="disease">Disease</option><option value="insect">Insect</option><option value="weed">Weed</option></select></Field>
        </div>

        <Field label="Description"><textarea disabled={readOnly} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={inputCls} /></Field>

        {/* Hosts */}
        <Field label="Host crop types">
          <div className="space-y-2">
            {!readOnly && <CropTypeCombobox value={null} onChange={addHost} />}
            <div className="flex flex-wrap gap-1.5">
              {hostIds.length === 0 && <span className="text-xs text-neutral-400">No hosts — the pest won't auto-match any farms.</span>}
              {hostIds.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                  {cropTypeNames[id] || `#${id}`}
                  {!readOnly && <button onClick={() => removeHost(id)} className="hover:text-emerald-900" aria-label={`Remove ${cropTypeNames[id] || id}`}><X className="h-3 w-3" /></button>}
                </span>
              ))}
            </div>
          </div>
        </Field>

        {/* Model archetype */}
        <Field label="Forecasting model">
          <select disabled={readOnly} value={modelType} onChange={(e) => onTypeChange(e.target.value as PestModelType)} className={inputCls}>
            <option value="generic_infection">Infection — Magarey (most fungal diseases: brown rot, mildews, botrytis…)</option>
            <option value="infection_wetness">Infection — Mills table (apple &amp; pear scab / black spot)</option>
            <option value="degree_day">Degree-day (insect lifecycle — e.g. LBAM, codling moth)</option>
            <option value="risk_index">Risk index (generic daily favourability)</option>
          </select>
        </Field>

        {profile?.model_rationale && (
          <p className="text-xs text-neutral-500 leading-relaxed -mt-2">
            <span className="font-medium text-neutral-600">Why this model: </span>{profile.model_rationale}
          </p>
        )}

        <div className="rounded-lg border border-neutral-200 p-3 bg-neutral-50/50">
          <div className="text-xs font-medium text-neutral-500 mb-2 flex items-center gap-1.5">{TYPE_ICON[modelType]} Model parameters</div>
          <ParamsEditor type={modelType} params={params} setParams={setParams} readOnly={readOnly} />
        </div>

        <Field label={<span className="flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> Recommended actions</span>}>
          <textarea disabled={readOnly} value={recommended} onChange={(e) => setRecommended(e.target.value)} rows={3} className={inputCls} placeholder="What to do when risk is high…" />
        </Field>

        {!readOnly && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button variant="primary" onClick={save} disabled={saving || !name.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {model ? 'Save changes' : 'Create pest'}
            </Button>
            {onCancel && <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>}
            <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>
            {error && <span className="text-xs text-red-600 basis-full">{error}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Params editors ──────────────────────────────────────────────────────────

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-50 disabled:text-neutral-500';

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumberField({ label, value, onChange, readOnly, step }: { label: string; value: number | undefined; onChange: (n: number) => void; readOnly: boolean; step?: number }) {
  return (
    <Field label={label}>
      <input type="number" step={step ?? 1} disabled={readOnly} value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} className={inputCls} />
    </Field>
  );
}

function WetnessSourceField({ value, onChange, readOnly }: { value: string | undefined; onChange: (v: string) => void; readOnly: boolean }) {
  return (
    <Field label="Wetness source">
      <select disabled={readOnly} value={value ?? 'auto'} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="auto">Modelled from humidity &amp; rain (works with forecast)</option>
        <option value="sensor">On-farm leaf-wetness sensor (observed only)</option>
      </select>
    </Field>
  );
}

const PREVIEW_TEMPS = [5, 10, 15, 20, 25, 30];

function ParamsEditor({ type, params, setParams, readOnly }: {
  type: PestModelType; params: Record<string, unknown>; setParams: (p: Record<string, unknown>) => void; readOnly: boolean;
}) {
  const set = (key: string, val: unknown) => setParams({ ...params, [key]: val });

  if (type === 'degree_day') {
    const p = params as unknown as DegreeDayParams;
    const events = p.events ?? [];
    const setEvents = (next: DegreeDayEvent[]) => set('events', next);
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Base temp (°C)" value={p.base_temp_c} onChange={(n) => set('base_temp_c', n)} readOnly={readOnly} step={0.1} />
          <NumberField label="Upper cutoff (°C)" value={p.upper_temp_c} onChange={(n) => set('upper_temp_c', n)} readOnly={readOnly} step={0.1} />
        </div>
        <div>
          <div className="text-xs font-medium text-neutral-600 mb-1">Lifecycle events (degree-day thresholds)</div>
          <div className="space-y-2">
            {events.map((ev, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input disabled={readOnly} value={ev.name} onChange={(e) => setEvents(events.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Event" className="border border-neutral-300 rounded px-2 py-1 text-sm flex-1 min-w-[120px] disabled:bg-neutral-50" />
                <input disabled={readOnly} type="number" value={ev.dd} onChange={(e) => setEvents(events.map((x, j) => (j === i ? { ...x, dd: Number(e.target.value) } : x)))} className="border border-neutral-300 rounded px-2 py-1 text-sm w-20 disabled:bg-neutral-50" title="Degree-days" />
                <select disabled={readOnly} value={ev.risk ?? 'moderate'} onChange={(e) => setEvents(events.map((x, j) => (j === i ? { ...x, risk: e.target.value as RiskLevel } : x)))} className="border border-neutral-300 rounded px-2 py-1 text-sm disabled:bg-neutral-50">
                  {RISK_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                {!readOnly && <button onClick={() => setEvents(events.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-red-600" aria-label="Remove event"><Trash2 className="h-4 w-4" /></button>}
                {ev.action && <div className="basis-full text-xs text-neutral-500 pl-1">{ev.action}</div>}
              </div>
            ))}
            {!readOnly && <button onClick={() => setEvents([...events, { name: '', dd: 0, risk: 'moderate', action: '' }])} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-neutral-100 hover:bg-neutral-200"><Plus className="h-3.5 w-3.5" /> Add event</button>}
          </div>
        </div>
      </div>
    );
  }

  if (type === 'infection_wetness') {
    const p = params as unknown as InfectionParams;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <NumberField label="RH wet threshold (%)" value={p.rh_threshold_pct} onChange={(n) => set('rh_threshold_pct', n)} readOnly={readOnly} />
          <NumberField label="Min temp (°C)" value={p.min_temp_c} onChange={(n) => set('min_temp_c', n)} readOnly={readOnly} step={0.5} />
          <NumberField label="Max temp (°C)" value={p.max_temp_c} onChange={(n) => set('max_temp_c', n)} readOnly={readOnly} step={0.5} />
          <NumberField label="Dry break (h)" value={p.dry_break_hours} onChange={(n) => set('dry_break_hours', n)} readOnly={readOnly} />
        </div>
        <WetnessSourceField value={p.wetness_source} onChange={(v) => set('wetness_source', v)} readOnly={readOnly} />
        <p className="text-xs text-neutral-400">The infection (Mills) table is kept as-is. Duration of leaf wetness at each temperature determines light / moderate / severe infection.</p>
      </div>
    );
  }

  if (type === 'generic_infection') {
    const p = params as unknown as GenericInfectionParams;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <NumberField label="Min temp (°C)" value={p.t_min_c} onChange={(n) => set('t_min_c', n)} readOnly={readOnly} step={0.5} />
          <NumberField label="Optimum (°C)" value={p.t_opt_c} onChange={(n) => set('t_opt_c', n)} readOnly={readOnly} step={0.5} />
          <NumberField label="Max temp (°C)" value={p.t_max_c} onChange={(n) => set('t_max_c', n)} readOnly={readOnly} step={0.5} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <NumberField label="Min wetness (h)" value={p.w_min_h} onChange={(n) => set('w_min_h', n)} readOnly={readOnly} step={0.5} />
          <NumberField label="Max wetness (h)" value={p.w_max_h} onChange={(n) => set('w_max_h', n)} readOnly={readOnly} />
          <NumberField label="Dry break (h)" value={p.dry_break_hours} onChange={(n) => set('dry_break_hours', n)} readOnly={readOnly} />
          <NumberField label="RH wet threshold (%)" value={p.rh_threshold_pct} onChange={(n) => set('rh_threshold_pct', n)} readOnly={readOnly} />
        </div>
        <WetnessSourceField value={p.wetness_source} onChange={(v) => set('wetness_source', v)} readOnly={readOnly} />
        <div>
          <div className="text-xs font-medium text-neutral-600 mb-1">Hours of leaf wetness needed to infect</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {PREVIEW_TEMPS.map((t) => {
              const w = magareyWetnessRequired(t, p);
              return (
                <div key={t} className="rounded-md bg-white border border-neutral-200 px-2 py-1.5 text-center">
                  <div className="text-[11px] text-neutral-500">{t} °C</div>
                  <div className={`text-sm font-semibold ${w == null ? 'text-neutral-300' : 'text-neutral-800'}`}>{w == null ? 'no risk' : `${w.toFixed(w < 10 ? 1 : 0)} h`}</div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-neutral-400 mt-1.5">Magarey et al. (2005). A wet period that lasts this long at its mean temperature is an infection; 1.5× and 2× grade it moderate and severe.</p>
        </div>
      </div>
    );
  }

  const p = params as unknown as RiskIndexParams;
  const bands = p.bands ?? {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <NumberField label="Optimal min (°C)" value={p.opt_temp_min} onChange={(n) => set('opt_temp_min', n)} readOnly={readOnly} step={0.5} />
        <NumberField label="Optimal max (°C)" value={p.opt_temp_max} onChange={(n) => set('opt_temp_max', n)} readOnly={readOnly} step={0.5} />
        <NumberField label="RH threshold (%)" value={p.rh_threshold_pct} onChange={(n) => set('rh_threshold_pct', n)} readOnly={readOnly} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(['low', 'moderate', 'high', 'severe'] as const).map((b) => (
          <NumberField key={b} label={`${b} ≥ score`} value={bands[b]} onChange={(n) => set('bands', { ...bands, [b]: n })} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}
